/**
 * Item Categories Service
 *
 * CRUD for workspace_item_categories and workspace_category_questions.
 * The six builtin categories (problem, opportunity, risk, solution,
 * commitment, investment) are seeded per workspace. Admins can toggle
 * them on/off and add custom categories with their own altitude questions.
 */

import { getSupabaseClient } from './supabaseClient';

export interface WorkspaceItemCategory {
  id: string;
  workspaceId: string;
  slug: string;
  label: string;
  color: string | null;
  description: string | null;
  isActive: boolean;
  isBuiltin: boolean;
  sortOrder: number;
  createdAt: string;
  /** Labels for the Item Types grid, keyed by altitude level (1–5). Null when not set. */
  altitudeLabels: Record<1 | 2 | 3 | 4 | 5, string | null>;
}

export interface WorkspaceCategoryQuestion {
  id: string;
  categoryId: string;
  dimension: 'timeHorizon' | 'workSize' | 'impactSurface';
  questionText: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  option5: string;
}

const CAT_TABLE = 'workspace_item_categories';
const Q_TABLE = 'workspace_category_questions';


function rowToCategory(row: Record<string, unknown>): WorkspaceItemCategory {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    slug: row.slug as string,
    label: row.label as string,
    color: (row.color as string) || null,
    description: (row.description as string) || null,
    isActive: row.is_active as boolean,
    isBuiltin: row.is_builtin as boolean,
    sortOrder: row.sort_order as number,
    createdAt: row.created_at as string,
    altitudeLabels: {
      5: (row.altitude_label_5 as string) || null,
      4: (row.altitude_label_4 as string) || null,
      3: (row.altitude_label_3 as string) || null,
      2: (row.altitude_label_2 as string) || null,
      1: (row.altitude_label_1 as string) || null,
    },
  };
}

function rowToQuestion(row: Record<string, unknown>): WorkspaceCategoryQuestion {
  return {
    id: row.id as string,
    categoryId: row.category_id as string,
    dimension: row.dimension as WorkspaceCategoryQuestion['dimension'],
    questionText: row.question_text as string,
    option1: row.option_1 as string,
    option2: row.option_2 as string,
    option3: row.option_3 as string,
    option4: row.option_4 as string,
    option5: row.option_5 as string,
  };
}

const CAT_SELECT = 'id, workspace_id, slug, label, color, description, is_active, is_builtin, sort_order, created_at, altitude_label_5, altitude_label_4, altitude_label_3, altitude_label_2, altitude_label_1';

/** Fetch all categories for a workspace (all, not just active). */
export async function listItemCategories(
  workspaceId: string
): Promise<{ data: WorkspaceItemCategory[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(CAT_TABLE)
    .select(CAT_SELECT)
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: true });

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    // Seed the defaults and return real DB-backed rows so callers always
    // operate on real UUIDs — never fake client-side IDs.
    const { error: seedError } = await supabase.rpc('seed_workspace_item_categories', {
      p_workspace_id: workspaceId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(CAT_TABLE)
      .select(CAT_SELECT)
      .eq('workspace_id', workspaceId)
      .order('sort_order', { ascending: true });

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToCategory), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToCategory), error: null };
}

/** Toggle is_active for a category. */
export async function setCategoryActive(
  id: string,
  isActive: boolean
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(CAT_TABLE).update({ is_active: isActive }).eq('id', id);
  return { error: error?.message ?? null };
}

/** Create a new custom category. */
export async function createCategory(
  workspaceId: string,
  slug: string,
  label: string,
  color: string | null,
  description: string | null,
  sortOrder: number,
  altitudeLabels?: Record<1 | 2 | 3 | 4 | 5, string | null>
): Promise<{ data: WorkspaceItemCategory | null; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(CAT_TABLE)
    .insert({
      workspace_id: workspaceId,
      slug,
      label,
      color: color || null,
      description: description || null,
      is_active: true,
      is_builtin: false,
      sort_order: sortOrder,
      ...(altitudeLabels
        ? {
            altitude_label_5: altitudeLabels[5] || null,
            altitude_label_4: altitudeLabels[4] || null,
            altitude_label_3: altitudeLabels[3] || null,
            altitude_label_2: altitudeLabels[2] || null,
            altitude_label_1: altitudeLabels[1] || null,
          }
        : {}),
    })
    .select(CAT_SELECT)
    .single();
  if (error) return { data: null, error: error.message };
  return { data: rowToCategory(data as Record<string, unknown>), error: null };
}

/** Update a category's label, color, description, or altitude labels. */
export async function updateCategory(
  id: string,
  updates: Partial<Pick<WorkspaceItemCategory, 'label' | 'color' | 'description' | 'sortOrder' | 'altitudeLabels'>>
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const dbUpdates: Record<string, unknown> = {};
  if (updates.label !== undefined) dbUpdates.label = updates.label;
  if (updates.color !== undefined) dbUpdates.color = updates.color;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
  if (updates.altitudeLabels !== undefined) {
    dbUpdates.altitude_label_5 = updates.altitudeLabels[5] || null;
    dbUpdates.altitude_label_4 = updates.altitudeLabels[4] || null;
    dbUpdates.altitude_label_3 = updates.altitudeLabels[3] || null;
    dbUpdates.altitude_label_2 = updates.altitudeLabels[2] || null;
    dbUpdates.altitude_label_1 = updates.altitudeLabels[1] || null;
  }
  const { error } = await supabase.from(CAT_TABLE).update(dbUpdates).eq('id', id);
  return { error: error?.message ?? null };
}

/** Delete a custom (non-builtin) category. */
export async function deleteCategory(id: string): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(CAT_TABLE).delete().eq('id', id).eq('is_builtin', false);
  return { error: error?.message ?? null };
}

/** Fetch altitude questions for a category. */
export async function listCategoryQuestions(
  categoryId: string
): Promise<{ data: WorkspaceCategoryQuestion[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(Q_TABLE)
    .select('id, category_id, dimension, question_text, option_1, option_2, option_3, option_4, option_5')
    .eq('category_id', categoryId);
  if (error) return { data: [], error: error.message };
  return { data: (data as Record<string, unknown>[]).map(rowToQuestion), error: null };
}

/** Upsert a question for a category dimension. */
export async function upsertCategoryQuestion(
  categoryId: string,
  question: Omit<WorkspaceCategoryQuestion, 'id' | 'categoryId'>
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(Q_TABLE).upsert(
    {
      category_id: categoryId,
      dimension: question.dimension,
      question_text: question.questionText,
      option_1: question.option1,
      option_2: question.option2,
      option_3: question.option3,
      option_4: question.option4,
      option_5: question.option5,
    },
    { onConflict: 'category_id,dimension' }
  );
  return { error: error?.message ?? null };
}
